import { useCallback, useState } from 'react';
import { LangText } from '@/components';
import { preFixImgUrl } from '@/utils';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import { DownOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/** 与 react-router ScrollRestoration 配合：返回首页时恢复轮播索引，避免首屏高度与离开时不一致 */
const HOME_BANNER_SLIDE_KEY = 'jdx-home-banner-real-index';

export default function Home() {
  const [activeTab, setActiveTab] = useState<'phone' | 'email'>('phone');
  const [inputValue, setInputValue] = useState('');

  const bannerList = [
    {
      img: preFixImgUrl('8be865710903814efb4a85e8c0c815a1.png'),
    },
    {
      img: preFixImgUrl('36ec34a97933620e6d9176acfec97482.png'),
    },
    {
      img: preFixImgUrl('cdc1ac34d4bed7b577edbc2a0950d37e.png'),
    }
  ];

  const [bannerInitialSlide] = useState(() => {
    if (typeof window === 'undefined') return 0;
    try {
      const raw = sessionStorage.getItem(HOME_BANNER_SLIDE_KEY);
      if (raw == null) return 0;
      const n = parseInt(raw, 10);
      if (Number.isFinite(n) && n >= 0 && n < bannerList.length) return n;
    } catch {
      /* ignore */
    }
    return 0;
  });

  const handleBannerSlideChange = useCallback((swiper: { realIndex: number }) => {
    try {
      sessionStorage.setItem(HOME_BANNER_SLIDE_KEY, String(swiper.realIndex));
    } catch {
      /* ignore */
    }
  }, []);

  const navigate = useNavigate();

  const openInNewTab = (path: string) => {
    window.open(path, '_blank', 'noopener,noreferrer');
  };

  const goSignup = () => {
    const value = inputValue.trim();
    if (value) {
      navigate('/signup', { state: { account: value, type: activeTab } });
    } else {
      navigate('/signup');
    }
  };

  const { t } = useTranslation();

  /** manusSlug 与 MANUS_THEME_SLUGS / 线上路径一致，点击后全页 iframe 嵌入 Manus 主题站 */
  const categories = [
    { key: 'a1b2c3d4', manusSlug: 'onsen' as const, icon: preFixImgUrl('04fd7c6bfdc050d2de376eb693163593.png') },
    { key: 'b2c3d4e5', manusSlug: 'food' as const, icon: preFixImgUrl('65ac59d3e71e22c47604f5fc9fdff4fd.png') },
    { key: 'c3d4e5f6', manusSlug: 'shopping' as const, icon: preFixImgUrl('c797972ac9b76f066e0849d7b6e8bf5c.png') },
    { key: 'd4e5f6g7', manusSlug: 'attractions' as const, icon: preFixImgUrl('9dbaa918774829206eaa2b36c29531d1.png') },
    { key: 'e5f6g7h8', manusSlug: 'hotels' as const, icon: preFixImgUrl('d9bd5c694d6e5503cbfe503328a4f3f5.png') },
    { key: 'f6g7h8i9', manusSlug: 'culture' as const, icon: preFixImgUrl('17dd4284a9352e8c4001be78bdcb9419.png') },
    { key: 'g7h8i9j0', manusSlug: 'family' as const, icon: preFixImgUrl('906c1e16c373b490ad1dc032e0556a1e.png') },
    { key: 'h8i9j0k1', manusSlug: 'couples' as const, icon: preFixImgUrl('18745529d9892a4f1c14871cbbee4b66.png') },
    { key: 'i9j0k1l2', manusSlug: 'camping' as const, icon: preFixImgUrl('050bc9eb3a0552ab65ee293a6aa3f815.png') },
    { key: 'j0k1l2m3', manusSlug: 'skiing' as const, icon: preFixImgUrl('16d712e92f1f2da64579875f5ac4daab.png') },
    { key: 'k1l2m3n4', manusSlug: 'beach' as const, icon: preFixImgUrl('e34ca65d097c45659f01b326935b618d.png') },
  ];

  return (
    <div className="bg-[#FFF]">
      <div className="relative -mt-20">
        {/* 固定首屏高度 + 恢复轮播索引，避免浏览器后退时像素级 ScrollRestoration 与变高的 Banner 错位 */}
        <div className="relative w-full aspect-[16/9] max-h-[min(90vh,960px)] min-h-[260px]">
          <Swiper
            modules={[Autoplay]}
            autoplay={{ delay: 3000, disableOnInteraction: false, pauseOnMouseEnter: true }}
            loop
            speed={1000}
            className="w-full h-full"
            initialSlide={bannerInitialSlide}
            onSlideChange={handleBannerSlideChange}
          >
            {bannerList.map((item, index) => (
              <SwiperSlide key={index} className="relative w-full h-full">
                <img
                  src={item.img}
                  alt={`Banner ${index + 1}`}
                  className="object-cover absolute inset-0 w-full h-full"
                />
              </SwiperSlide>
            ))}
          </Swiper>
          <div className="flex absolute inset-0 z-10">
              <div className="flex flex-col justify-center mx-auto w-full px-25 max-w-12xl">
                {/* 大标语 - 左对齐 */}
                <div className="mt-4 mb-24 text-left mt-18">
                  <h1 className="text-white text-[48px] font-bold leading-[1.5]">
                    <LangText lngKey="4dfa4c15">{t("4dfa4c15")}</LangText>、
                    <LangText lngKey="3b51fcbb">{t("3b51fcbb")}</LangText>、<br />
                    <LangText lngKey="779b742a">{t("779b742a")}</LangText>。
                  </h1>
                </div>

                {/* AI 旅行规划师 - 左对齐 */}
                <div className="mb-4">
                  <div className="flex gap-2 items-center">
                    <div className="relative flex-shrink-0 w-14 h-14">
                      <img src={preFixImgUrl('e296d8a1d512b9fd555d8e6044afbb09.png')} alt="AI 旅行规划师" className="absolute inset-0 w-full h-full object-contain animate-[spin_15s_linear_infinite]" />
                      <img src={preFixImgUrl('80abef239b3cf578e666fdfdebeb9f98.png')} alt="" className="absolute inset-0 w-[40%] h-[40%] m-auto object-contain" />
                    </div>
                    <LangText lngKey="30cbf182" className="text-white text-[30px] font-bold ">
                      {t('30cbf182')}
                    </LangText>
                  </div>
                </div>

                {/* 搜索栏 - 宽度100% */}
                <div className="mb-16">
                  <div className="w-full">
                    <div className="relative rounded-[22px] p-[3px]">
                      <div
                        className="absolute inset-0 rounded-[22px] pointer-events-none marquee-border"
                        style={{
                          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                          WebkitMaskComposite: 'xor',
                          maskComposite: 'exclude',
                          padding: '3px',
                          zIndex: 0,
                          '--angle': '0deg',
                        } as React.CSSProperties & { [key: string]: string | number }}
                      />
                      <style>{`
                        @property --angle {
                          syntax: '<angle>';
                          initial-value: 0deg;
                          inherits: false;
                        }
                        .marquee-border {
                          background: conic-gradient(from var(--angle), #ec4899, #a855f7, #3b82f6, #adff2b, #ec4899);
                          animation: marquee 3s linear infinite;
                        }
                        @keyframes marquee {
                          0% {
                            --angle: 0deg;
                          }
                          100% {
                            --angle: 360deg;
                          }
                        }
                      `}</style>
                      <div className="relative rounded-[16px] flex flex-row items-center py-3 px-10 gap-0 bg-[rgba(61,61,61,0.5)] z-10">
                        {/* 您想去哪里 */}
                        <div className="flex flex-1 gap-2 items-center pl-4 min-w-0 cursor-pointer" onClick={() => navigate('/ai-trip')}>
                          <img src={preFixImgUrl('81bf988fc9674d3a3523ba75379588f0.png')} alt="" className="w-[24px] h-[24px] flex-shrink-0 object-contain" />
                          <LangText className="text-[#E2E2E2] text-[20px] truncate" lngKey="e8a1b2c3" />
                          <DownOutlined className="text-[14px] flex-shrink-0 font-bold" style={{ color: '#E2E2E2' }} />
                        </div>
                        {/* 人数 */}
                        <div className="flex flex-1 gap-2 items-center min-w-0 cursor-pointer" onClick={() => navigate('/ai-trip')}>
                          <img src={preFixImgUrl('51970ed528a646636f167da9d309a732.png')} alt="" className="w-[24px] h-[24px] flex-shrink-0 object-contain" />
                          <LangText className="text-[#E2E2E2] text-[20px] truncate" lngKey="f9b2c3d4" />
                          <DownOutlined className="text-[14px] flex-shrink-0 font-bold" style={{ color: '#E2E2E2' }} />
                        </div>
                        {/* 预算 */}
                        <div className="flex flex-1 gap-2 items-center min-w-0 cursor-pointer" onClick={() => navigate('/ai-trip')}>
                          <img src={preFixImgUrl('50c2316dd55f2e3b03fc86a77da872ec.png')} alt="" className="w-[24px] h-[24px] flex-shrink-0 object-contain" />
                          <LangText className="text-[#E2E2E2] text-[20px] truncate" lngKey="a3c4d5e6" />
                          <DownOutlined className="text-[14px] flex-shrink-0 font-bold" style={{ color: '#E2E2E2' }} />
                        </div>
                        {/* 右侧按钮 */}
                        <button className="px-8 py-4 bg-[linear-gradient(270deg,#ECEBFF,#FFFFFF)] rounded-[24px] font-medium hover:opacity-90 transition-opacity text-[16px] whitespace-nowrap ml-4 mr-4 shrink-0" onClick={() => navigate('/ai-trip')}>
                          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#5652FF] to-[#9C59FF]">
                            <LangText className="text-[20px] font-bold leading-[1]" lngKey="b4d5e6f7" />
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 热门度假类别 - 左对齐 */}
                {/* <div className="w-full">
                  <h2 className="text-white text-[30px] font-bold text-left mb-8 leading-tight">
                    <LangText lngKey="4248e263">{t("4248e263")}</LangText>
                  </h2>
                  
                  <div className="grid grid-cols-11 w-full">
                    {categories.map((category) => (
                      <button
                        key={category.key}
                        type="button"
                        onClick={() => openInNewTab(`/${category.manusSlug}`)}
                        className="flex flex-col gap-4 items-center p-0 bg-transparent border-0 transition-opacity cursor-pointer hover:opacity-90 group text-inherit"
                      >
                        <div className="w-[38px] h-[38px]">
                          <img src={category.icon} alt="" className="w-full h-full" />
                        </div>
                        <LangText lngKey={category.key} className="text-white text-[18px] font-normal text-center" />
                      </button>
                    ))}
                  </div>
                </div> */}
              </div>
            </div>
        </div>
      </div>

      <div className="py-28 w-full px-25 bg-[#FFF] mx-auto max-w-12xl">
        {/* 旅程每一步 */}
        <div className='text-transparent bg-clip-text bg-gradient-to-r from-[#5652FF] to-[#9C59FF] text-[40px] font-bold mb-10'>
          <LangText lngKey="62070da9" />
        </div>
        <div className='flex overflow-visible relative flex-col items-center py-12 mb-6 w-full'>
          <div className='relative w-full aspect-[1669/139] overflow-visible'>
            <img src={preFixImgUrl('7e7e73d0765cb2bedfaa157af75793dd.png')} alt="" className='object-contain absolute inset-0 w-full h-full' aria-hidden />
            {[
              { lngKey: '3d469075' },
              { lngKey: '52582d68' },
              { lngKey: '4de5158f' },
            ].map((item, i) => (
              <span
                key={item.lngKey}
                className='ellipse-dot absolute rounded-full py-4 px-5 text-[22px] font-bold text-white shadow-[0_2px_8px_rgba(86,82,255,0.3)] bg-gradient-to-r from-[#5652FF] to-[#9C59FF]'
                style={{ animationDelay: `${i * -(30 / 3)}s` }}
              >
                <LangText lngKey={item.lngKey} />
              </span>
            ))}
          </div>
        </div>
        <style>{`
          /* 参考 codepen.io/mmdgirl/RZVgoq：椭圆 x 半径 a、y 半径 b，dot 位移 translate(a*cos(θ),b*sin(θ)) */
          @property --angle { syntax: '<angle>'; initial-value: 0deg; inherits: false; }
          @keyframes ellipse-orbit {
            to { --angle: 360deg; }
          }
          /* 长直径 100%（a=50% 宽），长:短=1669:139 → b=50% 高；用 left/top 相对容器，避免 translate 的 % 相对自身 */
          .ellipse-dot {
            --a: 50%;
            --b: 50%;
            left: calc(50% + var(--a) * cos(var(--angle)));
            top: calc(50% + var(--b) * sin(var(--angle)));
            transform: translate(-50%, -50%);
            animation: ellipse-orbit 30s linear infinite;
            white-space: nowrap;
            min-width: max-content;
          }
        `}</style>
        <div className='grid grid-cols-6 gap-6 w-full'>
          {[
            { titleKey: '6e124369', descKey: '4f295f0c', btnKey: 'cbc7c477', url: '/duty-free-pre-order', img: preFixImgUrl('38f716737c629dc1911eefad23783c8d.png') },
            { titleKey: '3cb2d47c', descKey: '688fef77', btnKey: 'ad8b9d1b', url: '/airport-luggage-delivery', img: preFixImgUrl('00cc7f23dc4c347e22d227e0e4470cd9.jpeg') },
            // { titleKey: '88f51026', descKey: '675e2635', btnKey: '95d2ae82', url: '/coupon-list', img: preFixImgUrl('df9cb693eee8799db764e510cf63121e.png') },
            { titleKey: '88f51026', descKey: '675e2635', btnKey: '95d2ae82', url: '/temp-coupon-list', img: preFixImgUrl('df9cb693eee8799db764e510cf63121e.png') },
            { titleKey: '62d6dd78', descKey: 'e59be857', btnKey: '6ae1ffa4', url: '/vip', img: preFixImgUrl('63ea383d55de1f58288ea59d811c7b51.png') },
            { titleKey: 'd168ae9c', descKey: 'a44c7f03', btnKey: 'c000a6c1', url: undefined, img: preFixImgUrl('8d659130aff221b1e4b431075cdafa98.png') },
            { titleKey: '04f8081f', descKey: 'f63a9a12', btnKey: '8d6b0eb9', url: undefined, img: preFixImgUrl('762446e20068bd5428968073a5d2e08f.png') },
          ].map((card) => (
            <div key={card.titleKey} className='flex flex-col rounded-2xl bg-white shadow-[0_4px_20px_rgba(0,0,0,0.06)] overflow-hidden pt-2 pb-5 px-2'>
              <div className='relative w-full aspect-[4/3] overflow-hidden rounded-t-2xl mb-4'>
                <img src={card.img} alt="" className='object-cover absolute inset-0 w-full h-full rounded-2xl' />
              </div>
              <div className='flex flex-col flex-1'>
                <h3 className='text-[22px] mb-2'>
                  <LangText lngKey={card.titleKey} />
                </h3>
                <p className='text-[14px] text-[#7F7F7F] leading-[1.5] mb-4 flex-1'>
                  <LangText lngKey={card.descKey} />
                </p>
                {card.url ? (
                  <button
                    type='button'
                    className='w-fit px-4 rounded-full text-white h-10 text-[16px] bg-[linear-gradient(270deg,#5652FF,#9C59FF)] hover:opacity-90 transition-opacity'
                    onClick={() => navigate(card.url!)}
                  >
                    <LangText lngKey={card.btnKey} />
                  </button>
                ) : (
                  <button
                    className='w-fit px-4 rounded-full h-10 text-[16px] bg-[#EAEAEA] text-[#878787]'
                  >
                    <LangText lngKey='7f0da2a2' />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className='flex my-20 w-full'>
          {[
            { img: preFixImgUrl('db92bc2d7238bdb0df1fb5baa1cea7a1.png'), titleKey: '264a217a', descKey: '3fb2937a' },
            { img: preFixImgUrl('de060d57d1d6d675c62ade0e2466e48c.png'), titleKey: '109b8bc1', descKey: '974ae094' },
            { img: preFixImgUrl('05d2f97be300d607a01fea277a846e52.png'), titleKey: '68b032a0', descKey: '1f5b8d1d' },
          ].map((item, index) => (
            <div
              key={item.titleKey}
              className={`flex-1 flex flex-col items-center text-center px-18 ${index > 0 ? 'border-l border-[#E5E5E5]' : ''}`}
            >
              <img src={item.img} alt="" className="object-contain mb-2 w-12 h-12" />
              <h3 className="text-[24px] font-bold text-[#1a1a1a] my-[24px]">
                <LangText lngKey={item.titleKey} />
              </h3>
              <p className="text-[16px] text-[#7F7F7F] leading-[1.5]">
                <LangText lngKey={item.descKey} />
              </p>
            </div>
          ))}
        </div>
        {/* 为什么选择我们 */}
        <div className='w-full'>
          <div className='flex flex-row gap-8 items-baseline mb-14'>
            <h2 className='flex-shrink-0 text-transparent bg-clip-text bg-gradient-to-r from-[#5652FF] to-[#9C59FF] text-[40px] font-bold'>
              <LangText lngKey='1d53aff2' />
            </h2>
            <p className='text-[20px] text-[#1a1a1a] leading-relaxed'>
              <LangText lngKey='c80379ac' />
            </p>
          </div>
          <div className='overflow-hidden relative w-full rounded-2xl'>
            <img
              src={preFixImgUrl('1ffb0dc2d17c964bd76f3443b361b250.png')}
              alt=""
              className='block object-cover w-full h-auto'
            />
            <div className='grid absolute inset-0 z-10 grid-cols-2 gap-6 place-content-center pl-10 w-full pr-[400px]'>
              {[
                { img: preFixImgUrl('1e8966b2fda2150b3b733558b5f2a3c5.png'), titleKey: '8d289351', descKey: 'c72405ad' },
                { img: preFixImgUrl('ab271ce47907c320e544535a0584f1f9.png'), titleKey: '5b72c3ab', descKey: '4cefccdb' },
                { img: preFixImgUrl('72a309d8c164590c2f440c5ee56fe19d.png'), titleKey: '411bae11', descKey: 'e9c1c402' },
                { img: preFixImgUrl('21ad70cbe6cd4a256be4efd04ee9ddcf.png'), titleKey: '325a9562', descKey: '6223f326' },
              ].map((item) => (
                <div
                  key={item.titleKey}
                  className='flex flex-col text-left px-6 py-9 rounded-[20px] bg-white/90 shadow-[0_4px_24px_rgba(0,0,0,0.08)]'
                >
                  <img src={item.img} alt="" className="object-contain mb-6 w-7 h-7" />
                  <h3 className='text-[20px] font-bold text-[#1a1a1a] mb-3'>
                    <LangText lngKey={item.titleKey} />
                  </h3>
                  <p className='text-[14px] text-[#666] leading-[1.6]'>
                    <LangText lngKey={item.descKey} />
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* 热门度假目的地 */}
        <div className='w-full'>
          <h2 className='text-transparent bg-clip-text bg-gradient-to-r from-[#5652FF] to-[#9C59FF] text-[40px] font-bold text-left my-14'>
            <LangText lngKey='d555124a' />
          </h2>
          <div className='grid grid-cols-3 gap-6'>
            {[
              { img: preFixImgUrl('ab9e25c762b6f3e20077f8aa248d3500.png'), titleKey: 'aa598538', slug: 'fuji' },
              { img: preFixImgUrl('cd1f4d4f66735352240908755bb9d110.png'), titleKey: '4e359440', slug: 'kiyomizu' },
              { img: preFixImgUrl('6b7be4eeb8005b6691252abb4de62a5a.png'), titleKey: '0be693c4', slug: 'sensoji' },
              { img: preFixImgUrl('b23ca5f2868fea74f0b44f4bb6579846.png'), titleKey: '051b9761', slug: 'nara' },
              { img: preFixImgUrl('1a3c5d99c1a27d9fa44e8a56f738cc9e.png'), titleKey: 'ed239a0f', slug: 'tokyo-tower' },
              { img: preFixImgUrl('1f40efb63dc2df2332d527486675a31e.png'), titleKey: 'b4069a51', slug: 'yasaka' },
            ].map((item) => (
              <div
                key={item.titleKey}
                role="button"
                tabIndex={0}
                className='relative cursor-pointer rounded-2xl overflow-hidden shadow-[0_4px_20px_rgba(0,0,0,0.08)] aspect-[505/304] transition-opacity hover:opacity-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5652FF]'
                onClick={() => openInNewTab(`/destination/${item.slug}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    openInNewTab(`/destination/${item.slug}`)
                  }
                }}
              >
                <img src={item.img} alt="" className='object-cover absolute inset-0 w-full h-full pointer-events-none' />
                <div className='absolute inset-x-0 bottom-0 px-6 py-7 bg-gradient-to-t to-transparent pointer-events-none from-black/60'>
                  <span className='text-white text-[24px] font-bold'>
                    <LangText lngKey={item.titleKey} />
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 合作商户 */}
        <div className='w-full'>
          <div className='flex flex-row gap-8 items-baseline my-14'>
            <h2 className='flex-shrink-0 text-transparent bg-clip-text bg-gradient-to-r from-[#5652FF] to-[#9C59FF] text-[40px] font-bold'>
              <LangText lngKey='42c5b7f2' />
            </h2>
            <p className='text-[20px] text-[#1a1a1a] leading-relaxed'>
              <LangText lngKey='82c82a9f' />
            </p>
          </div>
          <div className='rounded-2xl overflow-hidden bg-gradient-to-br from-[#5652FF] via-[#7c6aff] to-[#9C59FF] shadow-[0_8px 32px_rgba(86,82,255,0.25)] px-6 py-12'>
            <p className='flex flex-wrap justify-center items-baseline gap-x-1 text-center text-white text-[26px] mb-8'>
              <span><LangText lngKey='95dbe58e' /></span>
              <span className='text-[36px] font-bold'><LangText lngKey='e8f9a1b2' /></span>
              <span><LangText lngKey='3287a6a9' /></span>
            </p>
            <div className='px-8 pt-12 pb-10 rounded-xl backdrop-blur-sm bg-white/15'>
              <div className='flex flex-nowrap gap-x-8 justify-between items-start'>
                {[
                  { img: preFixImgUrl('0562123bf6955a494d5f0ac20196db70.png'), lngKey: 'c3d4e5f6' },
                  { img: preFixImgUrl('85a4872b880a0d2cf5afc69b5aea46d6.png'), lngKey: '3c759eea' },
                  { img: preFixImgUrl('fac4c0a4a8922ede951f0c26a6777879.png'), lngKey: 'e5f6g7h8' },
                  { img: preFixImgUrl('3a286b6079abccf9d67b5b21be2c259a.png'), lngKey: '4e367040' },
                  { img: preFixImgUrl('01340c195e4503eb31f048f51385540f.png'), lngKey: '78fa736c' },
                  { img: preFixImgUrl('f7b45bcd483cfa431c32329720564e10.png'), lngKey: 'c3d567d5' },
                  { img: preFixImgUrl('7d2fbb0ce204c9f1585d6c11d3bdb4e3.png'), lngKey: '809f2ef7' },
                  { img: preFixImgUrl('0a3ef256105ba14d90bc2e6a63d2d3ee.png'), lngKey: 'c45d7182' },
                  { img: preFixImgUrl('3a5669a0d9f5e819aa52772fdc51c5a2.png'), lngKey: '62d6dd78' },
                  { img: preFixImgUrl('cb55dddbfcb41be21eff5a5a0cb13e24.png'), lngKey: '04f8081f' },
                  { img: preFixImgUrl('c2e44ef4e85a3cbff8a3d84cf3296fb3.png'), lngKey: '53c2e152' },
                ].map((item) => (
                  <div key={item.lngKey} className='flex flex-col flex-1 gap-3 items-center'>
                    <img src={item.img} alt="" className='object-contain w-12 h-12' />
                    <span className='text-white text-[14px] font-medium text-center'>
                      <LangText lngKey={item.lngKey} />
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* 用户评价 - 自右向左自动滚动 */}
        <div className='w-full'>
          <h2 className='text-transparent bg-clip-text bg-gradient-to-r from-[#5652FF] to-[#9C59FF] text-[32px] font-bold text-left my-14'>
            <LangText lngKey='22a5b6ee' />
          </h2>
          <div className='rounded-2xl bg-[#F5F5F5] shadow-[0_4px 24px_rgba(0,0,0,0.06)] overflow-hidden p-12 pt-[100px]'>
            <div className='overflow-visible'>
              <div className='flex flex-nowrap w-max animate-marquee-left'>
                {[1, 2].map((copy) => (
                  <div key={copy} className='flex flex-nowrap gap-12 pr-12'>
                    {[
                      { avatar: preFixImgUrl('7ba9f7977c07218915165459fccaaf22.png'), travelerKey: 'aaf3fda7', locationKey: 'bf18cd40', reviewKey: '行李配送太方便了，環球影城輕鬆玩整天' },
                      { avatar: preFixImgUrl('4a7be0cbb3b075b5d7fc73ed134ac5e3.png'), travelerKey: 'af65d5a8', locationKey: 'fa52e55a', reviewKey: 'VIP service makes me feelparticularly prestigious,and theexclusive channel saves a lotof time' },
                      { avatar: preFixImgUrl('6a057560528da928bce92dd9411375e3.png'), travelerKey: '2d6282eb', locationKey: '5e8e9797', reviewKey: 'สัมภาระส่งตรงถึงโรงแรมทำให้การเดินทางง่ายขึ้นมาก' },
                      { avatar: preFixImgUrl('a6b02ba38b5e0a0302da86280fbae8fb.png'), travelerKey: 'fd237d8f', locationKey: '919dc3b6', reviewKey: '行前购很实用，下了飞机就可以直接去玩了' },
                      { avatar: preFixImgUrl('ec4f3916cc94956abff390bd1d597d75.png'), travelerKey: 'c28c78a1', locationKey: '370eada0', reviewKey: '中文界面太友好了，完全不需要担心语言的问题' },
                      { avatar: preFixImgUrl('2fcf8bf0542f45c75b3bf46138694b6a.png'), travelerKey: '1aa9355f', locationKey: '011a43c3', reviewKey: '쿠폰이 너무 수지가 맞아서가게에 미리 받으면 쓸 수 있다' },
                    ].map((review) => (
                      <div key={`${copy}-${review.reviewKey}`} className='flex-shrink-0 w-[240px] rounded-[20px] bg-white px-6 pt-0 pb-6 shadow-[0_2px_12px_rgba(0,0,0,0.06)]'>
                        <div className='flex flex-col items-center'>
                          <div className='flex justify-center -mt-10 mb-4'>
                            <img src={review.avatar} alt="" className='w-20 h-20 rounded-full object-cover ring-2 ring-white ring-offset-2 ring-offset-[#F5F5F5]' />
                          </div>
                          <span className='text-[20px] font-bold text-center my-2'><LangText lngKey={review.travelerKey} /></span>
                          <span className='text-[13px] text-[#999] mt-1 mb-3 text-center'><LangText lngKey={review.locationKey} /></span>
                          <div className='flex gap-0.5 mb-4 text-[20px] text-[#FFB800]'>★★★★★</div>
                          <p className='text-[14px] text-[#616161] leading-[1.6] text-center w-full'>{review.reviewKey}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <style>{`
          @keyframes marquee-left {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .animate-marquee-left {
            animation: marquee-left 40s linear infinite;
          }
        `}</style>
      </div>

      {/* 注册推广：背景图 + 手机图 + 文案与注册 + 二维码 */}
      <div
        className="relative w-full h-[clamp(500px,40vw,560px)] bg-cover bg-center bg-no-repeat overflow-hidden"
        style={{ backgroundImage: `url(${preFixImgUrl('599f8ee455a2cf793506ed074a645e47.png')})` }}
      >
        <div className="grid relative z-10 items-center mx-auto h-full w-fit grid-cols-[clamp(260px,25vw,320px)_minmax(360px,420px)_clamp(180px,17vw,220px)] gap-x-[clamp(40px,4vw,56px)]">
          <img
            src={preFixImgUrl('fa226d1692e61ece4e2be0f634280cb0.png')}
            alt=""
            className="self-end justify-self-start mb-[-60px] translate-x-[60px] w-[clamp(220px,18.75vw,280px)] h-auto object-contain object-left-bottom pointer-events-none"
          />
          <div className="flex flex-col col-span-2 w-full text-left">
            <h2 className="text-white text-[42px] font-bold leading-tight">
              <LangText lngKey="69589067" />
            </h2>
            <p className="text-white/90 text-[20px] mt-6">
              <LangText lngKey="5ee19df6" />
            </p>
            <div className="flex gap-[clamp(48px,5vw,72px)] items-center mt-14">
              <div className="flex flex-col">
                <div className="flex gap-2 mb-4">
                  <button
                    type="button"
                    onClick={() => setActiveTab('phone')}
                    className={`px-4 py-2 rounded-lg text-[18px] font-medium transition-colors ${
                      activeTab === 'phone'
                        ? 'bg-[#A6A5A6] text-white opacity-90'
                        : 'bg-transparent text-white'
                    }`}
                  >
                    <LangText lngKey="d009c3b3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('email')}
                    className={`px-4 py-2 rounded-lg text-[18px] font-medium transition-colors ${
                      activeTab === 'email'
                        ? 'bg-[#A6A5A6] text-white opacity-90'
                        : 'bg-transparent text-white'
                    }`}
                  >
                    <LangText lngKey="9b1c6681" />
                  </button>
                </div>
                <div className="rounded-2xl bg-[#A6A5A6]/90 p-2 w-[420px]">
                  <div className="flex overflow-hidden items-center">
                    <input
                      type={activeTab === 'phone' ? 'tel' : 'email'}
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      placeholder={activeTab === 'email' ? t('dddc316d') : t('c92b76cd')}
                      className="flex-1 min-w-0 px-4 py-3 bg-transparent text-white text-[16px] placeholder:text-white/90"
                      aria-label="input"
                    />
                    <button
                      type="button"
                      onClick={goSignup}
                      className="flex-shrink-0 px-12 py-3 rounded-[100px] text-[20px] font-bold bg-white hover:opacity-90 transition-opacity ml-4"
                    >
                      <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#A75AFF] to-[#4B51FF]">
                        <LangText lngKey="7a19c71b" />
                      </span>
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-3 items-center shrink-0">
                <img
                  src={preFixImgUrl('6fd9ac42f5bfb2652c1c323134230749.png')}
                  alt="QR Code"
                  className="w-[clamp(136px,11vw,160px)] h-[clamp(136px,11vw,160px)] object-contain"
                />
                <LangText
                  lngKey="819b05d8"
                  className="text-center text-white text-[17px] leading-snug max-w-[220px]"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  );
}
